import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Heart, MessageSquare, Share2, Flag, Send, 
  Loader2, MoreHorizontal, X, Trash2, Clock, User, MapPin
} from 'lucide-react';
import api from '../utils/api';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to get current user
const useCurrentUser = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  return { user, loading };
};

const POST_TYPES = {
  general: { label: 'General', icon: '💬' },
  deal_alert: { label: 'Deal Alert', icon: '🔥' },
  question: { label: 'Question', icon: '❓' },
  showcase: { label: 'Showcase', icon: '🏆' },
  news: { label: 'News', icon: '📰' }
};

const REPORT_REASONS = [
  { id: 'spam', label: 'Spam' },
  { id: 'scam', label: 'Scam or fraud' },
  { id: 'harassment', label: 'Harassment or bullying' },
  { id: 'adult_content', label: 'Adult content' },
  { id: 'hate', label: 'Hate speech' },
  { id: 'illegal_item', label: 'Illegal item' },
  { id: 'other', label: 'Other' }
];

// Report Modal Component
const ReportModal = ({ contentType, contentId, onClose, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!reason) {
      setError('Please select a reason');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = contentType === 'post' 
        ? `/api/community/posts/${contentId}/report`
        : `/api/community/comments/${contentId}/report`;
      
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ reason, details })
      });
      
      if (res.ok) {
        onSuccess?.();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit report');
      }
    } catch (e) {
      setError('Failed to submit report');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Report {contentType}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-2 mb-4">
          {REPORT_REASONS.map((r) => (
            <label key={r.id} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input 
                type="radio" 
                name="reason" 
                value={r.id} 
                checked={reason === r.id}
                onChange={(e) => setReason(e.target.value)}
              />
              <span>{r.label}</span>
            </label>
          ))}
        </div>
        
        <textarea
          placeholder="Additional details (optional)"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          className="w-full p-3 border rounded-lg text-sm mb-4"
          rows={3}
        />
        
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button 
            onClick={handleSubmit}
            disabled={loading || !reason}
            className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Comment Component
const Comment = ({ comment, currentUser, onDelete, onLike }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(comment.like_count || 0);
  const [showMenu, setShowMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const handleLike = async () => {
    if (!currentUser) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community/comments/${comment.comment_id}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLiked(data.action === 'liked');
      setLikeCount(data.like_count);
    } catch (e) {}
  };

  return (
    <div className="flex gap-3 py-4 border-b last:border-0">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
        {comment.user_image ? (
          <img src={comment.user_image} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          comment.user_name?.charAt(0)?.toUpperCase() || '?'
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-gray-900 text-sm">{comment.user_name}</span>
          <span className="text-xs text-gray-400">
            {new Date(comment.created_at).toLocaleDateString()}
          </span>
        </div>
        <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.text_body}</p>
        <div className="flex items-center gap-4 mt-2">
          <button 
            onClick={handleLike}
            className={`flex items-center gap-1 text-xs ${liked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
          >
            <Heart className={`h-4 w-4 ${liked ? 'fill-current' : ''}`} />
            {likeCount > 0 && likeCount}
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="text-gray-400 hover:text-gray-600"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {showMenu && (
              <div className="absolute left-0 top-6 bg-white shadow-lg rounded-lg border py-1 z-10 min-w-[120px]">
                <button 
                  onClick={() => { setShowReportModal(true); setShowMenu(false); }}
                  className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2 text-red-600"
                >
                  <Flag className="h-3 w-3" /> Report
                </button>
                {currentUser?.user_id === comment.user_id && (
                  <button 
                    onClick={() => { onDelete?.(comment.comment_id); setShowMenu(false); }}
                    className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 flex items-center gap-2 text-red-600"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showReportModal && (
        <ReportModal 
          contentType="comment"
          contentId={comment.comment_id}
          onClose={() => setShowReportModal(false)}
          onSuccess={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

// Main Post Detail Page
const CommunityPostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/community/posts/${postId}`);
        if (!res.ok) throw new Error('Post not found');
        const data = await res.json();
        setPost(data);
        setLikeCount(data.like_count || 0);

        // Check if user liked
        if (user) {
          const token = localStorage.getItem('token');
          const likedRes = await fetch(`${API_URL}/api/community/posts/${postId}/liked`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const likedData = await likedRes.json();
          setLiked(likedData.liked);
        }

        // Fetch comments
        const commentsRes = await fetch(`${API_URL}/api/community/posts/${postId}/comments`);
        const commentsData = await commentsRes.json();
        setComments(commentsData.comments || []);
      } catch (e) {
        setError(e.message || 'Failed to load post');
      }
      setLoading(false);
    };

    fetchPost();
  }, [postId, user]);

  // Set page metadata for SEO
  useEffect(() => {
    if (post) {
      document.title = `${post.text_body?.substring(0, 60)}... | BidZuu Community`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', post.text_body?.substring(0, 160) || 'BidZuu Community Post');
      }
    }
    return () => {
      document.title = 'BidZuu - Community Marketplace';
    };
  }, [post]);

  const handleLike = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community/posts/${postId}/like`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLiked(data.action === 'liked');
      setLikeCount(data.like_count);
    } catch (e) {}
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ text_body: newComment })
      });

      if (res.ok) {
        const data = await res.json();
        setComments(prev => [...prev, data.comment]);
        setNewComment('');
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to post comment');
      }
    } catch (e) {
      alert('Failed to post comment');
    }
    setSubmitting(false);
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/community/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
    } catch (e) {
      alert('Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} />
        <div className="h-[184px]"></div>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} />
        <div className="h-[184px]"></div>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-gray-500 mb-4">{error || 'Post not found'}</p>
          <Link to="/community" className="text-emerald-600 hover:underline">
            Back to Community
          </Link>
        </div>
      </div>
    );
  }

  const typeInfo = POST_TYPES[post.type] || POST_TYPES.general;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} />
      <div className="h-[184px]"></div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/community')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Community
        </button>

        {/* Post Card */}
        <article className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-semibold">
                  {post.user_info?.profile_image ? (
                    <img src={post.user_info.profile_image} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    post.user_name?.charAt(0)?.toUpperCase() || '?'
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{post.user_name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {new Date(post.created_at).toLocaleString()}
                    </span>
                    {post.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {post.location}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                {typeInfo.icon} {typeInfo.label}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-800 whitespace-pre-wrap text-lg leading-relaxed">
              {post.text_body}
            </p>
          </div>

          {/* Media */}
          {post.media_urls?.length > 0 && (
            <div className={`grid gap-1 ${
              post.media_urls.length === 1 ? '' : 
              post.media_urls.length === 2 ? 'grid-cols-2' : 
              'grid-cols-2 md:grid-cols-3'
            }`}>
              {post.media_urls.map((url, idx) => (
                <img 
                  key={idx} 
                  src={url} 
                  alt="" 
                  className="w-full aspect-square object-cover cursor-pointer hover:opacity-90"
                  onClick={() => window.open(url, '_blank')}
                />
              ))}
            </div>
          )}

          {/* Video */}
          {post.video_url && (
            <div className="aspect-video bg-black">
              <video src={post.video_url} controls className="w-full h-full" />
            </div>
          )}

          {/* Attached Listing */}
          {post.attached_listing && (
            <Link 
              to={`/item/${post.attached_listing.listing_id}`}
              className="m-6 p-4 bg-gray-50 rounded-xl flex items-center gap-4 hover:bg-gray-100 transition"
            >
              {post.attached_listing.images?.[0] && (
                <img 
                  src={post.attached_listing.images[0]} 
                  alt="" 
                  className="w-20 h-20 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <p className="font-medium text-gray-900">{post.attached_listing.title}</p>
                <p className="text-emerald-600 font-semibold text-lg">
                  ${post.attached_listing.price?.toLocaleString()}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  post.attached_listing.status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {post.attached_listing.status}
                </span>
              </div>
            </Link>
          )}

          {/* Actions */}
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button 
                onClick={handleLike}
                className={`flex items-center gap-2 font-medium ${
                  liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                }`}
              >
                <Heart className={`h-6 w-6 ${liked ? 'fill-current' : ''}`} />
                <span>{likeCount}</span>
              </button>
              <div className="flex items-center gap-2 text-gray-500">
                <MessageSquare className="h-6 w-6" />
                <span>{comments.length}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={handleShare} className="text-gray-500 hover:text-blue-500">
                <Share2 className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setShowReportModal(true)}
                className="text-gray-500 hover:text-red-500"
              >
                <Flag className="h-5 w-5" />
              </button>
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Comments ({comments.length})
          </h3>

          {/* Comment Form */}
          {user ? (
            <form onSubmit={handleSubmitComment} className="mb-6">
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                  {user.first_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    className="w-full p-3 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                    rows={3}
                    data-testid="comment-input"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      type="submit"
                      disabled={!newComment.trim() || submitting}
                      className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2"
                      data-testid="comment-submit"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Post Comment
                    </button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-gray-600 mb-2">Sign in to leave a comment</p>
              <Link 
                to="/auth" 
                className="inline-block px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600"
              >
                Sign In
              </Link>
            </div>
          )}

          {/* Comments List */}
          {comments.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No comments yet. Be the first!</p>
          ) : (
            <div className="divide-y">
              {comments.map((comment) => (
                <Comment
                  key={comment.comment_id}
                  comment={comment}
                  currentUser={user}
                  onDelete={handleDeleteComment}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          contentType="post"
          contentId={postId}
          onClose={() => setShowReportModal(false)}
          onSuccess={() => {
            setShowReportModal(false);
            alert('Report submitted. Thank you for helping keep our community safe.');
          }}
        />
      )}
    </div>
  );
};

export default CommunityPostDetail;
